import { ConfigProvider } from 'antd';
import ThemeConfigProvider from './styles/ThemeConfig';
import { createRoot } from 'react-dom/client';
import App from './App';
import { Logger, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import type { IPublicClientApplication, AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import AuthError from './components/AuthError';
import { groupRead, groupWrite, isBrowserStackLocal, isMockEnabled } from './helper/authms';

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  // @ts-ignore
  declare const CONFIG;
  const { clientId, authority, redirectUri } = CONFIG.authms;

  const instanceConfig = {
    auth: {
      clientId: clientId,
      authority: authority,
      redirectUri: redirectUri
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false
    }
  }

  let instance;
  if (isBrowserStackLocal() && isMockEnabled()) {
    const roleFromStorage = localStorage.getItem('userRole');
    
    let userRole;
    let group;

    if (roleFromStorage === 'admin') {
      userRole = "admin"
      group = groupWrite
    } else if (roleFromStorage === 'support') {
      userRole = "support"
      group = groupRead
    }

    console.warn(`Running in mock MSAL mode with role set to ${roleFromStorage}`);

    const userType = userRole === "admin" ? "admin" : "support"

    const mockAccount = {
      name: userType.toUpperCase(),
      username: `${userType}-mockuser@example.com`,
      homeAccountId: 'mock-home-id',
      localAccountId: 'mock-local-id',
      environment: 'login.microsoftonline.com',
      tenantId: 'mock-tenant',
      idTokenClaims: {
        name: 'Mock User',
        preferred_username: `${userType}-mockuser@example.com`,
        oid: 'mock-oid',
        groups: [group],
      },
    };
    instance  = {
      getAllAccounts: () => [mockAccount],
      getActiveAccount: () => mockAccount,
      setActiveAccount: () => {},
      // deepcode ignore HardcodedNonCryptoSecret: <Only mock token for test mode>
      loginPopup: () => Promise.resolve({ accessToken: 'mock-token' } as AuthenticationResult),
      logoutPopup: () => Promise.resolve(),
      // deepcode ignore HardcodedNonCryptoSecret: <Only mock token for test mode>
      acquireTokenSilent: () => Promise.resolve({ accessToken: 'mock-token' } as AuthenticationResult),
      initialize: () => Promise.resolve(),
      initializeWrapperLibrary: () => {},
      addEventCallback: () => null,
      removeEventCallback: () => {},
      getLogger: () =>
        new Logger(
          {
            loggerCallback: () => {},
            piiLoggingEnabled: false,
            logLevel: LogLevel.Info,
          },
          "MockLogger"
        ),
    };
  } else {
    instance = new PublicClientApplication(instanceConfig);
  }

  instance.initialize().then(() => {
    root.render(
      <MsalProvider instance={instance as IPublicClientApplication}>
        <ConfigProvider theme={ThemeConfigProvider}>
          <App />
        </ConfigProvider>
      </MsalProvider>
    );
  }).catch((error) => {
    console.error("MSAL Initialization failed:", error);
    return <AuthError error={undefined} message={"We couldn't log you in. Please try again later or contact Nimbus."} />;
  });
}
